/** API of the product lines referential (port of ProductLineService). */
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/shared/api/client';
import { deleteFile, uploadFile } from '@/shared/api/upload';
import type { PictureTimestamps, WithId } from '@/shared/api/types';

export {
  productLineKeyboardSchema,
  productLineSchema,
  type KeyboardSettings,
  type ProductLine,
  type ProductLineKeyboard,
} from '@/shared/config/product-line';
import {
  productLineSchema,
  type ProductLine,
} from '@/shared/config/product-line';

export const productLinesQueryOptions = () =>
  queryOptions({
    queryKey: ['product-lines'] as const,
    queryFn: () =>
      api.get<ProductLine[]>('product-line', {
        schema: z.array(productLineSchema),
      }),
    retry: false,
  });

export const productLineQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['product-lines', id] as const,
    queryFn: () =>
      api.get<ProductLine>(`product-line/${encodeURIComponent(id)}`, {
        schema: productLineSchema,
      }),
    retry: false,
  });

export type ProductLinePictureType = 'texture' | 'custom';

/** Payload of the product line dialog. */
export interface ProductLineInput extends PictureTimestamps<'texture'> {
  productLine: string;
  commercialProductLine: string;
  display: string;
  productGroup: string;
  sav: boolean;
  shadowType: string | null;
  sizeDisplay: string | null;
  stores: string[];
  commercialProductLineTranslations: Record<string, string>;
}

/**
 * PUT product-line is a partial update: the product line dialog sends its
 * input, the keyboards and XY dialogs only the fields of the record they edit.
 */
export type ProductLineUpdate = WithId<
  Partial<Omit<ProductLine, 'id'> & PictureTimestamps<ProductLinePictureType>>
>;

export const createProductLine = (data: ProductLineInput) =>
  api.post<ProductLine>('product-line', data);

export const updateProductLine = (data: ProductLineUpdate) =>
  api.put<unknown>('product-line', data);

export const uploadProductLinePicture = (
  id: string,
  file: File,
  type: ProductLinePictureType,
) => uploadFile(`product-line/${encodeURIComponent(id)}/upload/${type}`, file);

export const deleteProductLinePicture = (
  id: string,
  type: ProductLinePictureType,
) => deleteFile(`product-line/${encodeURIComponent(id)}/${type}`);
